import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { App as CapApp } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';
import { LocalNotifications } from '@capacitor/local-notifications';
import useCapacitorNotifications from '../hooks/useCapacitorNotifications';

const CapacitorInit = ({ userId }) => {
  const { 
    isNative, 
  } = useCapacitorNotifications(userId);

  // Guard against React.StrictMode double-mount in dev and against any other
  // accidental re-init: native plugin setup (StatusBar, Keyboard, listeners)
  // must run exactly once per app session.
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    console.log('[FLICKER]', performance.now().toFixed(0), 'ms — CapacitorInit mounted, platform=' + Capacitor.getPlatform());
    const initCapacitor = async () => {
      const platform = Capacitor.getPlatform();
      
      if (platform === 'ios' || platform === 'android') {
        // Hide splash screen with failsafe: ensure it ALWAYS hides even if there's an error
        // Timeout wrapper guarantees splash hides within 500ms max
        try {
          console.log('[FLICKER]', performance.now().toFixed(0), 'ms — calling SplashScreen.hide()');
          await Promise.race([
            SplashScreen.hide(),
            new Promise((resolve) => setTimeout(resolve, 500))
          ]);
          console.log('[FLICKER]', performance.now().toFixed(0), 'ms — SplashScreen.hide() resolved');
        } catch (err) {
          console.error('[CapacitorInit] SplashScreen.hide failed, forcing hide:', err);
          // Try one more time without await to ensure it's called
          SplashScreen.hide().catch(() => {});
        }
        
        // StatusBar config is set declaratively in capacitor.config.json
        // (overlaysWebView: false, style: DARK, backgroundColor: #000000).
        // Do NOT reconfigure here at runtime — it would run after first paint
        // and cause a visible status-bar flash. Capacitor applies the JSON
        // config natively before the WebView mounts, so this is sufficient.

        // Fix iOS keyboard interactions:
        // 1. When keyboard opens, scroll the focused element into view
        // 2. When keyboard closes, force a layout reflow so the webview
        //    reclaims the space that the keyboard occupied.
        if (platform === 'ios') {
          try {
            await Keyboard.addListener('keyboardWillShow', () => {
              // Scroll the active/focused element into view so it's not hidden
              requestAnimationFrame(() => {
                const focused = document.activeElement;
                if (focused && (focused.tagName === 'INPUT' || focused.tagName === 'TEXTAREA')) {
                  focused.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            });
            await Keyboard.addListener('keyboardWillHide', () => {
              // Trigger a minimal scroll to force WebKit to recompute layout
              const el = document.getElementById('main-scroll-container');
              if (el) {
                const { scrollTop } = el;
                el.style.height = '100%';
                requestAnimationFrame(() => {
                  el.style.height = '';
                  el.scrollTop = scrollTop;
                });
              }
            });
          } catch (err) {
            console.log('Keyboard listener error:', err);
          }
        }
        
        // Handle app state changes
        await CapApp.addListener('appStateChange', ({ isActive }) => {
          console.log('App state changed. Is active:', isActive);
        });
        
        // Handle back button on Android
        if (platform === 'android') {
          await CapApp.addListener('backButton', ({ canGoBack }) => {
            if (!canGoBack) {
              CapApp.exitApp();
            } else {
              window.history.back();
            }
          });
        }
      }
    };

    initCapacitor();

    return () => {
      CapApp.removeAllListeners();
      if (Capacitor.getPlatform() === 'ios') {
        Keyboard.removeAllListeners().catch(() => {});
      }
    };
  }, []);

  // Cancel any leftover local notifications – daily reminders are now handled
  // entirely by the backend via FCM push, so local scheduling is disabled.
  useEffect(() => {
    if (isNative && !userId) {
      console.log('User logged out, cancelling all local notifications');
      LocalNotifications.cancelAll().catch(err => 
        console.error('Error cancelling notifications on logout:', err)
      );
    } else if (isNative && userId) {
      // Cancel legacy local notifications (id 1001 daily, id 1002 evening)
      LocalNotifications.cancel({ notifications: [{ id: 1001 }, { id: 1002 }] }).catch(() => {});
    }
  }, [isNative, userId]);

  return null; // This component doesn't render anything
};

export default CapacitorInit;
